import React, { useState, useEffect } from 'react';
import {
  Phone,
  MessageSquare,
  Wifi,
  Settings,
  ChevronDown,
  ChevronUp,
  Users,
  DollarSign,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { serviceAPI } from '../services/api';

// Import operator logos
import unitelLogo from '../assets/logos/unitellogo.png';
import ltcLogo from '../assets/logos/ltclogo.png';
import tplusLogo from '../assets/logos/tpluslogo.png';
import etlLogo from '../assets/logos/etllogo.png';
import bestLogo from '../assets/logos/bestlogo.png';

const ByServiceView = ({ month, year }) => {
  const [expandedService, setExpandedService] = useState(null);
  const [serviceData, setServiceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const monthParam = month != null ? month : new Date().getMonth() + 1;
  const yearParam = year != null ? year : new Date().getFullYear();

  // Operator logos mapping
  const operatorLogos = {
    UNITEL: unitelLogo,
    LTC: ltcLogo,
    TPLUS: tplusLogo,
    ETL: etlLogo,
    BEST: bestLogo
  };

  // Service categories configuration
  const serviceCategories = {
    voice: {
      name: 'Voice Services',
      icon: Phone,
      color: 'blue'
    },
    sms: {
      name: 'SMS Services',
      icon: MessageSquare,
      color: 'green'
    },
    data: {
      name: 'Data Services',
      icon: Wifi,
      color: 'purple'
    },
    vas: {
      name: 'Value Added Services',
      icon: Settings,
      color: 'indigo'
    }
  };

  useEffect(() => {
    fetchServiceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchServiceData = async () => {
    try {
      setLoading(true);
      
      // Use predefined service categories directly instead of fetching from API
      const serviceCategoryKeys = ['voice', 'sms', 'data', 'vas'];
      const processedData = {};
      
      // Initialize each service category
      for (const category of serviceCategoryKeys) {
        processedData[category] = {
          name: serviceCategories[category]?.name || category.charAt(0).toUpperCase() + category.slice(1) + ' Services',
          icon: serviceCategories[category]?.icon || Settings,
          color: serviceCategories[category]?.color || 'gray',
          totalSubscribers: 0,
          totalActiveSubscribers: 0,
          totalRevenue: 0,
          avgCompliance: 0,
          avgGrowth: 0,
          operators: {}
        };
        
        // Fetch detailed data for this category from the API (pass month/year for filtered data)
        try {
          const categoryData = await serviceAPI.getByCategory(category, {
            month: monthParam,
            year: yearParam
          });
          console.log(`Fetched data for ${category}:`, categoryData);

          if (categoryData && categoryData.data && categoryData.data.operators && categoryData.data.operators.length > 0) {
            processedData[category].operators = {};
            
            let totalCompliance = 0;
            let totalGrowth = 0;
            let operatorCount = 0;
            let totalRevenue = 0;
            let totalSubscribers = 0;
            let totalActiveSubscribers = 0;
            
            categoryData.data.operators.forEach(op => {
              const operatorKey = op.name || op.operator_name;
              processedData[category].operators[operatorKey] = {
                logo: operatorLogos[operatorKey] || '/default-logo.png',
                color: op.color || 'gray',
                subscribers: op.total_subscribers || 0,
                activeSubscribers: op.active_subscribers || 0,
                marketShare: op.market_share || 0,
                revenue: op.total_revenue || 0,
                compliance: op.compliance_rate || 0,
                growth: op.growth_rate || 0,
                issues: [],
                strengths: []
              };
              
              totalCompliance += parseFloat(op.compliance_rate || 0);
              totalGrowth += parseFloat(op.growth_rate || 0);
              totalRevenue += parseFloat(op.total_revenue || 0);
              totalSubscribers += parseInt(op.total_subscribers || 0);
              totalActiveSubscribers += parseInt(op.active_subscribers || 0);
              operatorCount++;
            });
            
            processedData[category].totalSubscribers = totalSubscribers;
            processedData[category].totalActiveSubscribers = totalActiveSubscribers;
            processedData[category].totalRevenue = totalRevenue;
            processedData[category].avgCompliance = operatorCount > 0 ? (totalCompliance / operatorCount) : 0;
            processedData[category].avgGrowth = operatorCount > 0 ? (totalGrowth / operatorCount) : 0;
          }
        } catch (err) {
          console.error(`Error fetching data for category ${category}:`, err);
          // Add fallback data structure
          processedData[category].operators = {};
        }
      }
      
      console.log('Final processed service data:', processedData);
      setServiceData(processedData);
      
      // Set default expanded service to the first available service with operators
      const serviceKeys = Object.keys(processedData);
      const firstServiceWithData = serviceKeys.find(key => 
        processedData[key].operators && Object.keys(processedData[key].operators).length > 0
      );
      if (firstServiceWithData && !expandedService) {
        setExpandedService(firstServiceWithData);
      } else if (serviceKeys.length > 0 && !expandedService) {
        setExpandedService(serviceKeys[0]);
      }
    } catch (err) {
      console.error('Error fetching service data:', err);
      setError('Failed to load service data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error Loading Data</h3>
                <p className="text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toString();
  };

  const formatKip = (amount) => {
    if (!amount || amount === 0) return '0 ₭';
    const numAmount = parseFloat(amount);
    
    if (numAmount >= 1000000000) {
      return `${(numAmount / 1000000000).toFixed(1)}B ₭`;
    } else if (numAmount >= 1000000) {
      return `${(numAmount / 1000000).toFixed(1)}M ₭`;
    } else if (numAmount >= 1000) {
      return `${(numAmount / 1000).toFixed(1)}K ₭`;
    }
    
    return new Intl.NumberFormat('en-US').format(numAmount) + ' ₭';
  };

  const getServiceIcon = (serviceKey, service) => {
    const IconComponent = service?.icon || Settings;
    const colorClass = service?.color ? `text-${service.color}-600` : 'text-gray-600';
    return <IconComponent className={`h-6 w-6 ${colorClass}`} />;
  };

  const getComplianceColor = (compliance) => {
    if (compliance >= 95) return 'text-green-600';
    if (compliance >= 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getComplianceIcon = (compliance) => {
    if (compliance >= 95) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (compliance >= 90) return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    return <XCircle className="h-4 w-4 text-red-600" />;
  };

  if (Object.keys(serviceData).length === 0) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">No service data available.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderServiceSummary = (serviceKey, service) => {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <Users className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Total Subscribers</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatNumber(service.totalSubscribers)}
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <DollarSign className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Total Revenue</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatKip(service.totalRevenue)}
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Avg Compliance</span>
          </div>
          <div className={`text-2xl font-bold ${getComplianceColor(service.avgCompliance)}`}>
            {service.avgCompliance}%
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <TrendingUp className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Avg Growth</span>
          </div>
          <div className={`text-2xl font-bold ${service.avgGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {service.avgGrowth > 0 ? '+' : ''}{service.avgGrowth}%
          </div>
        </div>
      </div>
    );
  };

  const renderOperatorCard = (operatorName, operatorData) => {
    return (
      <div key={operatorName} className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {operatorData.logo && operatorData.logo !== '/default-logo.png' ? (
              <img 
                src={operatorData.logo} 
                alt={operatorName}
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center ${operatorData.logo && operatorData.logo !== '/default-logo.png' ? 'hidden' : ''}`}>
              <span className="text-blue-600 font-bold text-sm">
                {operatorName.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{operatorName}</h3>
              <p className="text-sm text-gray-600">{operatorData.marketShare}% market share</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getComplianceIcon(operatorData.compliance)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-sm text-gray-600 mb-1">Subscribers</div>
            <div className="text-xl font-bold text-gray-900">
              {formatNumber(operatorData.subscribers)}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Revenue</div>
            <div className="text-xl font-bold text-gray-900">
              {formatKip(operatorData.revenue)}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-gray-600">Compliance</span>
          <span className={`font-semibold ${getComplianceColor(operatorData.compliance)}`}>
            {operatorData.compliance}%
          </span>
        </div>

        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-gray-600">Growth</span>
          <span className={`font-semibold ${operatorData.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {operatorData.growth > 0 ? '+' : ''}{operatorData.growth}%
          </span>
        </div>

        {operatorData.issues && operatorData.issues.length > 0 && (
          <div className="mb-3">
            <div className="text-sm font-medium text-red-600 mb-1">Issues:</div>
            <ul className="text-xs text-red-700 space-y-1">
              {operatorData.issues.map((issue, index) => (
                <li key={index}>• {issue}</li>
              ))}
            </ul>
          </div>
        )}

        {operatorData.strengths && operatorData.strengths.length > 0 && (
          <div>
            <div className="text-sm font-medium text-green-600 mb-1">Strengths:</div>
            <ul className="text-xs text-green-700 space-y-1">
              {operatorData.strengths.map((strength, index) => (
                <li key={index}>• {strength}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Service Analysis Dashboard</h1>
          <p className="text-gray-600">Comprehensive view of telecom services across all operators</p>
        </div>

        <div className="space-y-6">
          {Object.entries(serviceData).map(([serviceKey, service]) => (
            <div key={serviceKey} className="bg-white rounded-lg shadow border border-emerald-200">
              <div
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedService(expandedService === serviceKey ? null : serviceKey)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getServiceIcon(serviceKey, service)}
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{service.name}</h2>
                      <p className="text-gray-600">
                        {`${formatNumber(service.totalSubscribers)} subscribers • ${formatKip(service.totalRevenue)} revenue`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className={`text-sm font-medium ${getComplianceColor(service.avgCompliance)}`}>
                        {service.avgCompliance}% Compliance
                      </div>
                      <div className={`text-sm ${service.avgGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {service.avgGrowth > 0 ? '+' : ''}{service.avgGrowth}% Growth
                      </div>
                    </div>
                    {expandedService === serviceKey ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {expandedService === serviceKey && (
                <div className="border-t border-gray-200 p-6">
                  {renderServiceSummary(serviceKey, service)}

                  <h3 className="text-lg font-semibold text-gray-900 border-orange-500 mb-4">Operator Performance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.entries(service.operators).map(([operatorName, operatorData]) =>
                      renderOperatorCard(operatorName, operatorData, serviceKey)
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ByServiceView;